import { getDragAndDropAttributes } from '@components/ArtBoard/DragAndDrop/getDragAndDropAttributes'
import { DRAGGABLE_TYPE } from '@components/ArtBoard/DragAndDrop/types'
import { Loading } from '@lib/components'
import type { BlockVisual, ID } from '@octopus-do/octopus-project-core'
import { Wireframe } from '@octopus-do/octopus-storybook'
import type React from 'react'

import { clsJoin } from '@/utils'
import lightOrDark from '@/utils/color/lightOrDark'
import { Name } from './Name'
import css from './styles.module.scss'

type Props = {
  id: ID
  index: number
  /** Goes into the drag payload next to the index. */
  dragData?: Record<string, unknown>
  componentRef?: React.RefObject<HTMLDivElement | null>
  title: string
  color: string
  visual: BlockVisual
  editing?: boolean
  highlighted?: boolean
  uploading?: boolean
  overlay?: boolean
  isLast?: boolean
  /** Inside the block, on top of the content: the note button of a project symbol. */
  note?: React.ReactNode
  /** Next to the block, anchored to the card: the toolbar, the popup. */
  children?: React.ReactNode
  onDragStart?: (e: React.PointerEvent<HTMLDivElement>) => void
  onClick?: () => void
  onRename?: (title: string) => void
  onSubmit?: () => void
}

/**
 * A symbol card of the sidebar, the same for a project symbol and for a
 * library one: the container decides where the data comes from and what
 * the edits do. The card is the anchor of its toolbar and popup.
 */
export const SymbolCard: React.FC<Props> = ({
  id,
  index,
  dragData = {},
  componentRef,
  title,
  color,
  visual,
  editing = false,
  highlighted = false,
  uploading = false,
  overlay = false,
  isLast = false,
  note,
  children,
  onDragStart = () => {},
  onClick = () => {},
  onRename = () => {},
  onSubmit = () => {},
}) => {
  const classNames = clsJoin(
    css.block,
    'symbol',
    highlighted && !overlay && css.active,
    lightOrDark(color) === 'dark' && css.light,
    lightOrDark(color) === 'light' && css.dark
  )

  return (
    <div
      className={clsJoin(css.container, isLast && css.last)}
      id={id}
      {...getDragAndDropAttributes(id, DRAGGABLE_TYPE.SYMBOL, {
        ...dragData,
        index,
      })}
    >
      <div
        ref={componentRef}
        className={classNames}
        style={{ '--symbol-color': color } as React.CSSProperties}
        onClick={onClick}
      >
        {!editing && (
          <div className={css.draggableContainer} onPointerDown={onDragStart} />
        )}

        {(title || editing) && (
          <div className={css.name}>
            <Name
              name={title}
              editing={editing}
              className={css.name_text}
              inputClassName={css.input}
              onRename={onRename}
              onSubmit={onSubmit}
            />
          </div>
        )}

        {visual.type === 'wireframes' && visual.wireframes.length > 0 && (
          <div className={css.wireframes}>
            {visual.wireframes.map((wireframe, i) => (
              <div className={css.wireframe} key={`${wireframe}-${i}`}>
                <Wireframe variant={wireframe} />
              </div>
            ))}
          </div>
        )}

        {visual.type === 'image' && !uploading && (
          <div className={css.image}>
            <img src={visual.image.thumbnailUrl} alt={visual.image.id} />
          </div>
        )}

        {uploading && (
          <div className={css.uploading}>
            <div>
              <Loading type="medium" color="gray" />
            </div>
          </div>
        )}

        {note}
      </div>

      {children}
    </div>
  )
}
